<script setup>
import { ref, onMounted, onUnmounted, watch } from "vue";
import BaseButton from "../ui/BaseButton.vue";
import IconMenu from "../icons/IconMenu.vue";
import IconClose from "../icons/IconClose.vue";
import IconChevron from "../icons/IconChevron.vue";
import IconMail from "../icons/IconMail.vue";
import { resolveIcon } from "../icons";
import { navLinks, site } from "../../content/site";
import { useLanding } from "../../composables/useLanding";
import { scrollToSection, scrollToLeadForm } from "../../composables/useScrollTo";
import logo from "@/assets/applates2_logo.png";
import { motion, useScroll, useSpring } from "motion-v";

const home = useLanding().path;

const condensed = ref(false);

// How far down the page the visitor is, drawn as a line under the bar.
const { scrollYProgress } = useScroll();
const progress = useSpring(scrollYProgress, { stiffness: 140, damping: 26, restDelta: 0.001 });
const drawerOpen = ref(false);

let queued = false;

function onScroll() {
  if (queued) return;
  queued = true;
  requestAnimationFrame(() => {
    condensed.value = window.scrollY > 24;
    queued = false;
  });
}

function go(target) {
  drawerOpen.value = false;
  scrollToSection(target);
}

function onCta(location) {
  drawerOpen.value = false;
  scrollToLeadForm(location);
}

// Stop the page scrolling behind the open drawer.
watch(drawerOpen, (open) => {
  document.body.style.overflow = open ? "hidden" : "";
});

function onKeydown(event) {
  if (event.key === "Escape") drawerOpen.value = false;
}

onMounted(() => {
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("keydown", onKeydown);
  onScroll();
});

onUnmounted(() => {
  window.removeEventListener("scroll", onScroll);
  window.removeEventListener("keydown", onKeydown);
  document.body.style.overflow = "";
});
</script>

<template>
  <header class="nav" :class="{ 'is-condensed': condensed }">
    <motion.span class="nav__progress" :style="{ scaleX: progress }" aria-hidden="true" />
    <div class="nav__inner container">
      <router-link :to="home" class="nav__brand" @click="drawerOpen = false">
        <img :src="logo" alt="" class="nav__logo" width="36" height="36" />
        <span class="nav__name">{{ site.nameEn }}</span>
      </router-link>

      <nav class="nav__links" aria-label="ניווט ראשי">
        <button
          v-for="link in navLinks"
          :key="link.target"
          class="nav__link"
          @click="go(link.target)"
        >
          {{ link.label }}
        </button>
      </nav>

      <div class="nav__actions">
        <BaseButton size="sm" @click="onCta('nav')">קבלו הדגמה</BaseButton>
      </div>

      <button
        class="nav__toggle"
        :aria-expanded="drawerOpen ? 'true' : 'false'"
        aria-controls="nav-drawer"
        :aria-label="drawerOpen ? 'סגירת התפריט' : 'פתיחת התפריט'"
        @click="drawerOpen = !drawerOpen"
      >
        <IconClose v-if="drawerOpen" />
        <IconMenu v-else />
      </button>
    </div>

    <!-- Dims the page behind the drawer; tapping it closes the drawer. -->
    <Transition name="fade">
      <div
        v-if="drawerOpen"
        class="nav__scrim"
        aria-hidden="true"
        @click="drawerOpen = false"
      />
    </Transition>

    <Transition name="drawer">
      <div
        v-if="drawerOpen"
        class="nav__drawer"
        id="nav-drawer"
      >
        <nav class="nav__drawer-links" aria-label="ניווט במובייל">
          <button
            v-for="(link, i) in navLinks"
            :key="link.target"
            class="nav__drawer-link"
            :style="{ '--item-index': i }"
            @click="go(link.target)"
          >
            <span class="nav__drawer-icon" aria-hidden="true">
              <component :is="resolveIcon(link.icon)" />
            </span>
            <span class="nav__drawer-label">{{ link.label }}</span>
            <IconChevron class="nav__drawer-arrow" aria-hidden="true" />
          </button>
        </nav>

        <div class="nav__drawer-cta">
          <p class="nav__drawer-cta-title">רוצים לראות איך זה עובד?</p>
          <p class="nav__drawer-cta-text">השאירו פרטים ונחזור אליכם עם הדגמה קצרה.</p>
          <BaseButton
            block
            size="lg"
            variant="inverse"
            @click="onCta('nav_drawer')"
          >
            קבלו הדגמה
          </BaseButton>
          <a class="nav__drawer-mail" :href="`mailto:${site.email}`">
            <IconMail aria-hidden="true" />
            <span>{{ site.email }}</span>
          </a>
        </div>
      </div>
    </Transition>
  </header>
</template>

<style scoped>
.nav {
  position: sticky;
  inset-block-start: 0;
  z-index: var(--z-nav);
  background: var(--glass);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-block-end: 1px solid transparent;
  transition: border-color var(--dur-base) var(--ease-in-out),
    background-color var(--dur-base) var(--ease-in-out),
    box-shadow var(--dur-base) var(--ease-in-out);
}

/* Grows from the reading edge as the page scrolls. Transform only. */
.nav__progress {
  position: absolute;
  inset-inline: 0;
  inset-block-end: -1px;
  block-size: 2px;
  background: linear-gradient(to left, var(--brand), var(--accent));
  transform-origin: right;
  opacity: 0;
  transition: opacity var(--dur-base) var(--ease-in-out);
}

.nav.is-condensed .nav__progress {
  opacity: 1;
}

[dir="ltr"] .nav__progress {
  transform-origin: left;
  background: linear-gradient(to right, var(--brand), var(--accent));
}

.nav.is-condensed {
  border-block-end-color: var(--line);
  background: var(--glass-solid);
  box-shadow: var(--lift-1);
}

.nav__inner {
  block-size: var(--nav-height);
  display: flex;
  align-items: center;
  gap: var(--space-5);
}

.nav__brand {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-shrink: 0;
}

.nav__logo {
  inline-size: 36px;
  block-size: 36px;
  border-radius: var(--radius-sm);
  transition: transform var(--dur-base) var(--ease-spring);
}

.nav__brand:hover .nav__logo {
  transform: scale(1.08) rotate(-4deg);
}

.nav__name {
  font-size: var(--text-lg);
  font-weight: var(--weight-bold);
  color: var(--ink);
  letter-spacing: var(--tracking-wide);
}

.nav__links {
  display: none;
  align-items: center;
  gap: var(--space-2);
  margin-inline-start: auto;
}

.nav__link {
  position: relative;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--ink-muted);
  transition: color var(--dur-fast) var(--ease-in-out);
}

.nav__link::after {
  content: "";
  position: absolute;
  inset-block-end: 2px;
  inset-inline: var(--space-3);
  block-size: 2px;
  border-radius: 2px;
  background: var(--brand);
  transform: scaleX(0);
  transform-origin: center;
  transition: transform var(--dur-base) var(--ease-out);
}

.nav__link:hover {
  color: var(--ink);
}

.nav__link:hover::after {
  transform: scaleX(1);
}

.nav__actions {
  display: none;
  flex-shrink: 0;
}

.nav__toggle {
  margin-inline-start: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  inline-size: 44px;
  block-size: 44px;
  border-radius: var(--radius-md);
  color: var(--ink);
  transition: background-color var(--dur-fast) var(--ease-in-out);
}

.nav__toggle:hover {
  background: var(--canvas-2);
}

.nav__toggle :deep(svg) {
  inline-size: 24px;
  block-size: 24px;
}

/* ---------------------------------------------------------------- drawer */

/*
 * Absolute, not fixed: .nav's backdrop-filter makes it the containing block
 * for fixed children. The nav is sticky at the top, so this still covers the
 * viewport below it.
 */
.nav__scrim {
  position: absolute;
  inset-inline: 0;
  inset-block-start: var(--nav-height);
  block-size: calc(100dvh - var(--nav-height));
  background: var(--scrim);
}

/* A floating panel rather than a full-width sheet. */
.nav__drawer {
  position: absolute;
  inset-inline: var(--space-3);
  inset-block-start: calc(var(--nav-height) + var(--space-2));
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  max-block-size: calc(100dvh - var(--nav-height) - var(--space-5));
  overflow-y: auto;
  padding: var(--space-3);
  background: var(--surface);
  border: var(--border);
  border-radius: var(--radius-2xl);
  box-shadow: var(--lift-3);
  transform-origin: top center;
}

.drawer-enter-active,
.drawer-leave-active {
  transition: opacity var(--dur-base) var(--ease-out),
    transform var(--dur-base) var(--ease-out);
}

.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
  transform: translate3d(0, -8px, 0) scale(0.98);
}

.nav__drawer-links {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.nav__drawer-link {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3);
  border-radius: var(--radius-lg);
  text-align: start;
  font-size: var(--text-lg);
  font-weight: var(--weight-semibold);
  color: var(--ink);
  transition: background-color var(--dur-fast) var(--ease-in-out);
  animation: drawer-item var(--dur-slow) var(--ease-out) both;
  animation-delay: calc(60ms + var(--item-index) * var(--stagger));
}

.nav__drawer-link:hover,
.nav__drawer-link:focus-visible {
  background: var(--brand-softer);
}

@keyframes drawer-item {
  from {
    opacity: 0;
    transform: translate3d(12px, 0, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

.nav__drawer-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  inline-size: 42px;
  block-size: 42px;
  border-radius: var(--radius-md);
  background: var(--brand-soft);
  color: var(--brand);
  transition: background-color var(--dur-fast) var(--ease-in-out),
    color var(--dur-fast) var(--ease-in-out);
}

.nav__drawer-link:hover .nav__drawer-icon {
  background: var(--brand);
  color: var(--on-brand);
}

.nav__drawer-icon :deep(svg) {
  inline-size: 20px;
  block-size: 20px;
}

.nav__drawer-label {
  flex: 1;
}

/* The chevron points down by default; a quarter turn points it forward (left in RTL). */
.nav__drawer-arrow {
  inline-size: 18px;
  block-size: 18px;
  flex-shrink: 0;
  color: var(--ink-subtle);
  transform: rotate(90deg);
  transition: transform var(--dur-base) var(--ease-out),
    color var(--dur-fast) var(--ease-in-out);
}

.nav__drawer-link:hover .nav__drawer-arrow {
  color: var(--brand);
  transform: rotate(90deg) translate3d(0, 4px, 0);
}

.nav__drawer-cta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-5);
  border-radius: var(--radius-xl);
  background: radial-gradient(
      120% 90% at 0% 100%,
      var(--brand-glow),
      transparent 60%
    ),
    var(--ink);
  text-align: center;
  animation: drawer-item var(--dur-slow) var(--ease-out) both;
  animation-delay: calc(60ms + 4 * var(--stagger));
}

.nav__drawer-cta-title {
  margin: 0;
  color: var(--on-ink);
  font-size: var(--text-lg);
  font-weight: var(--weight-bold);
}

.nav__drawer-cta-text {
  margin: 0 0 var(--space-2);
  color: var(--on-ink-muted);
  font-size: var(--text-sm);
}

.nav__drawer-mail {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  margin-block-start: var(--space-2);
  color: var(--on-ink-muted);
  font-size: var(--text-sm);
  text-decoration: none;
}

.nav__drawer-mail :deep(svg) {
  inline-size: 16px;
  block-size: 16px;
}

@media (min-width: 900px) {
  .nav__links,
  .nav__actions {
    display: flex;
  }

  .nav__toggle {
    display: none;
  }

  .nav__actions {
    margin-inline-start: var(--space-3);
  }
}
</style>
